import { useRef, useState } from 'react'
import type { ChatMessage } from '@/features/channel-router/chat-message'
import { checkStudioGenerationReadiness } from '@/features/generation-readiness/studio-readiness-check'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import {
  StudioGenerationBusyError,
  startAppGenerationFromPrompt,
} from '../services/start-app-generation-from-prompt'
import {
  buildConfirmedGenerationPrompt,
  takeGenerationReadinessProceed,
} from '../services/present-generation-readiness'
import { surfaceStudioGenerationFailure } from '../services/studio-generation-failure-surface'
import {
  beginProjectChatGeneration,
  bindProjectChatGeneration,
  projectChatOutcomeIdForSourceMessage,
  readProjectChatThread,
  settleProjectChatThreadDescriptor,
  settleProjectChatThreadOutcome,
  type ProjectChatOutcomeStatus,
  type ProjectChatThread,
  type ProjectChatThreadScope,
} from '../project-chat-thread'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import { readStoredAppGenerationJob } from '@/services/block-api-service'
import { readPersistedApplicationOntologyGenerationSelection } from '@/features/application-ontology/application-ontology-generation'
import { transferProjectChatHydrationOwnership } from './use-project-chat-thread-hydration'
import { CompactGenerationReadinessPanel } from './compact-generation-readiness-panel'

type GenerationReadinessMessage = Extract<
  ChatMessage,
  { kind: 'generation-readiness' }
>

export const GENERATION_PROJECT_RECOVERY_COPY: Record<
  LocaleCode,
  {
    inProgress: string
    completed: string
    blocked: string
    clarification: string
    busy: string
    failed: string
    authorityUnavailable: string
    claimUnavailable: string
  }
> = {
  en: {
    inProgress: 'App generation is in progress.',
    completed: 'App generation completed.',
    blocked: 'Generation cannot proceed until the readiness issue is resolved.',
    clarification: 'Generation needs more information before it can proceed.',
    busy: 'Another generation request is already in progress. Try again after it finishes.',
    failed: 'App generation failed. Review the message and try again.',
    authorityUnavailable: 'The original Application Ontology authority could not be restored. Submit the generation request again from the current project.',
    claimUnavailable: 'Could not save the generation start. Try again.',
  },
  ko: {
    inProgress: '앱 생성을 진행 중입니다.',
    completed: '앱 생성을 완료했습니다.',
    blocked: '준비 상태 문제를 해결하기 전에는 생성을 진행할 수 없습니다.',
    clarification: '생성을 진행하려면 추가 정보가 필요합니다.',
    busy: '다른 생성 요청이 진행 중입니다. 완료된 뒤 다시 시도해 주세요.',
    failed: '앱 생성에 실패했습니다. 메시지를 확인한 뒤 다시 시도해 주세요.',
    authorityUnavailable: '원래 Application Ontology 권한을 복원할 수 없습니다. 현재 프로젝트에서 생성 요청을 다시 제출해 주세요.',
    claimUnavailable: '생성 시작을 저장할 수 없습니다. 다시 시도해 주세요.',
  },
  ja: {
    inProgress: 'アプリを生成しています。',
    completed: 'アプリ生成が完了しました。',
    blocked: '準備状態の問題を解決するまで生成を続行できません。',
    clarification: '生成を続行するには追加情報が必要です。',
    busy: '別の生成リクエストが進行中です。完了後に再試行してください。',
    failed: 'アプリ生成に失敗しました。メッセージを確認して再試行してください。',
    authorityUnavailable: '元の Application Ontology 権限を復元できません。現在のプロジェクトから生成リクエストを再送信してください。',
    claimUnavailable: '生成開始を保存できませんでした。もう一度お試しください。',
  },
  zh: {
    inProgress: '正在生成应用。',
    completed: '应用生成已完成。',
    blocked: '解决准备状态问题前无法继续生成。',
    clarification: '继续生成前需要更多信息。',
    busy: '已有其他生成请求正在进行。请在其完成后重试。',
    failed: '应用生成失败。请查看消息后重试。',
    authorityUnavailable: '无法恢复原始 Application Ontology 权限。请从当前项目重新提交生成请求。',
    claimUnavailable: '无法保存生成启动状态。请重试。',
  },
  'zh-TW': {
    inProgress: '正在產生應用程式。',
    completed: '應用程式產生已完成。',
    blocked: '解決準備狀態問題前無法繼續產生。',
    clarification: '繼續產生前需要更多資訊。',
    busy: '已有其他產生要求正在進行。請在其完成後重試。',
    failed: '應用程式產生失敗。請查看訊息後重試。',
    authorityUnavailable: '無法還原原始 Application Ontology 權限。請從目前專案重新提交產生要求。',
    claimUnavailable: '無法儲存產生啟動狀態。請再試一次。',
  },
}

/**
 * Task 5.5 (ontology-ux-contract cycle) — the in-conversation host of
 * `<ReadinessPanel>`. One instance per `generation-readiness` chat message
 * (MessageRow-local state pattern, same as terminology-selection /
 * template-proposal: no store-level message-update mechanism exists, so the
 * post-presentation lifecycle — retry results, the one-shot launch — lives
 * here).
 *
 * Behavior contract (spec §3 + plan Task 5.5):
 * - The generate click resumes the SUBMITTING call site's own start block
 *   (the continuation registered by `presentGenerationReadinessInChat`), so
 *   the existing generation path — including the composer's abort/Stop
 *   wiring — runs unchanged. No start logic is duplicated here.
 * - The click is one-shot: the continuation is TAKEN from the registry and
 *   a ref guards the double-click window before React commits the launched
 *   state. After launch the panel collapses into a confirmation line — the
 *   generation's own progress/failure messages take over the thread.
 * - Retry (only reachable in the panel's `error` state) re-runs the SAME
 *   shared production judgment and swaps the report in place. In-flight
 *   guards are refs + early return, never a `disabled` attribute —
 *   disabled-button-0 applies to this card too.
 * - Fallback: if no continuation is registered (residual case — e.g. an HMR
 *   module swap dropped the registry while the store kept the message), the
 *   generate click still honors its promise by invoking the existing start
 *   entry directly with the message's verbatim prompt, with the same
 *   busy/failure surfacing the template-proposal decline path established.
 *   It never silently does nothing.
 */
export function GenerationReadinessChatCard({
  msg,
}: {
  msg: GenerationReadinessMessage
}) {
  const { locale } = useStudioLocale()
  const [report, setReport] = useState(msg.report)
  const [launched, setLaunched] = useState(false)
  const [claimFailure, setClaimFailure] = useState<string | null>(null)
  const [durableScope] = useState<ProjectChatThreadScope | null>(() => {
    const studio = useStudioStore.getState()
    const userId = studio.session?.user?.id
    const projectId = studio.currentProjectId
    return userId && projectId ? { userId, projectId } : null
  })
  const proceedTakenRef = useRef(false)
  const retryInFlightRef = useRef(false)

  if (launched) {
    return (
      <p
        data-testid="generation-readiness-launched"
        className="text-xs leading-5 text-muted-foreground"
      >
        생성을 시작했습니다. 진행 상황은 아래 메시지로 이어집니다.
      </p>
    )
  }

  const handleProceed = () => {
    // Synchronous ref guard: effective on a second click that lands before
    // the `launched` state commit re-renders (templateAcceptInFlightRef
    // precedent).
    if (proceedTakenRef.current) return
    proceedTakenRef.current = true
    setClaimFailure(null)
    // The confirm click is the human's decision to proceed WITH what this
    // panel showed — so the outgoing prompt states that, instead of
    // resending the pre-review draft byte-for-byte (the P5 defect: a
    // confirmation the user performs that changes nothing).
    const confirmedPrompt = buildConfirmedGenerationPrompt(msg.prompt, report)
    useStudioStore.getState().setLastSubmittedPrompt(confirmedPrompt)
    const copy = GENERATION_PROJECT_RECOVERY_COPY[locale]
    const outcomeId = projectChatOutcomeIdForSourceMessage(msg.id)
    const projectVisibleOutcome = (thread: ProjectChatThread | null) => {
      const outcome = thread?.turns.find(
        (turn) => turn.kind === 'outcome' && turn.id === outcomeId,
      )
      if (!outcome || outcome.kind !== 'outcome' || !durableScope) return false
      const studio = useStudioStore.getState()
      if (
        studio.session?.user?.id !== durableScope.userId ||
        studio.currentProjectId !== durableScope.projectId
      ) {
        return false
      }
      useStudioStore.setState({
        chatMessages: studio.chatMessages.map((message) =>
          message.id === msg.id || message.id === outcomeId
            ? { ...outcome, kind: 'project-chat-outcome' }
            : message,
        ),
      })
      transferProjectChatHydrationOwnership([msg.id, outcomeId], outcome.id)
      return true
    }
    const recoverClaimFailure = () => {
      setClaimFailure(copy.claimUnavailable)
      proceedTakenRef.current = false
    }
    const proceedClaim = takeGenerationReadinessProceed(msg.id, copy.inProgress)
    if (proceedClaim.kind === 'claimed') {
      setLaunched(true)
      proceedClaim.proceed(confirmedPrompt)
      return
    }
    if (proceedClaim.kind === 'owned') {
      if (!projectVisibleOutcome(proceedClaim.thread)) recoverClaimFailure()
      return
    }
    if (proceedClaim.kind === 'persistence-failure') {
      recoverClaimFailure()
      return
    }
    const restoredSelection =
      msg.generationAuthority === undefined
        ? readPersistedApplicationOntologyGenerationSelection(
            useStudioStore.getState().projectSettings,
          )
        : null
    const restoredBootstrap =
      msg.generationAuthority?.kind === 'application-ontology'
        ? msg.generationAuthority.bootstrap
        : restoredSelection?.kind === 'selected'
          ? restoredSelection.bootstrap
          : null
    const authorityUnavailable =
      msg.generationAuthority?.kind === 'unavailable' ||
      (msg.generationAuthority === undefined && restoredBootstrap === null)
    if (authorityUnavailable) {
      const timestamp = Date.now()
      if (durableScope) {
        const refused = settleProjectChatThreadDescriptor(durableScope, msg.id, {
          route: 'generation',
          status: 'refused',
          text: copy.authorityUnavailable,
          retryable: true,
          intent: msg.prompt,
          timestamp,
        })
        if (!projectVisibleOutcome(refused)) recoverClaimFailure()
      } else {
        setLaunched(true)
      }
      return
    }
    const pending = durableScope
      ? beginProjectChatGeneration(
          durableScope,
          msg.id,
          copy.inProgress,
          Date.now(),
        )
      : null
    projectVisibleOutcome(pending)
    if (durableScope && !pending) {
      const existing = readProjectChatThread(durableScope)
      if (!projectVisibleOutcome(existing)) recoverClaimFailure()
      return
    }
    if (!durableScope) setLaunched(true)
    const settleRestored = (
      status: Exclude<ProjectChatOutcomeStatus, 'pending'>,
      text: string,
      retryable: boolean,
    ) => {
      if (!durableScope) return
      const timestamp = Date.now()
      const terminal = settleProjectChatThreadOutcome(
        durableScope,
        outcomeId,
        { status, text, retryable, timestamp },
      ) ?? settleProjectChatThreadDescriptor(durableScope, msg.id, {
        route: 'generation',
        status,
        text,
        retryable,
        intent: msg.prompt,
        timestamp,
      })
      projectVisibleOutcome(terminal)
    }
    // Honest fallback — same shape as the template-proposal decline start.
    void startAppGenerationFromPrompt(confirmedPrompt, {
      ...(restoredBootstrap
        ? { applicationOntologyBootstrap: restoredBootstrap }
        : {}),
      onGenerationError: (message) =>
        useStudioStore.getState().setPendingChatNotice(message),
    })
      .then(async (started) => {
        if (started.status === 'clarification-required') {
          settleRestored('refused', copy.clarification, true)
          return
        }
        if (started.status === 'generation-readiness-blocked') {
          settleRestored('refused', copy.blocked, true)
          return
        }
        if (durableScope) {
          const descriptor = readStoredAppGenerationJob(started.projectId)
          projectVisibleOutcome(
            bindProjectChatGeneration(
              durableScope,
              outcomeId,
              {
                projectId: started.projectId,
                ...(descriptor?.requestId
                  ? { requestId: descriptor.requestId }
                  : {}),
                ...(descriptor?.jobId ? { jobId: descriptor.jobId } : {}),
              },
              Date.now(),
            ),
          )
        }
        await started.generation
        settleRestored('succeeded', copy.completed, false)
      })
      .catch((error) => {
        if (error instanceof StudioGenerationBusyError) {
          useStudioStore.getState().setPendingChatNotice(error.userMessage)
          settleRestored('refused', copy.busy, true)
          return
        }
        const surfaced = surfaceStudioGenerationFailure({
          error,
          intent: msg.prompt,
          locale,
        })
        settleRestored('failed', surfaced.notice || copy.failed, true)
      })
  }

  const handleRetry = () => {
    if (retryInFlightRef.current) return
    retryInFlightRef.current = true
    // checkStudioGenerationReadiness never rejects (it folds every failure
    // into a status:'error' report), so .then/.finally is the whole story.
    void checkStudioGenerationReadiness(
      useStudioStore.getState().currentProjectId ?? 'unsaved',
    )
      .then(setReport)
      .finally(() => {
        retryInFlightRef.current = false
      })
  }

  return (
    <>
      {claimFailure ? (
        <p
          className="mb-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          role="alert"
        >
          {claimFailure}
        </p>
      ) : null}
      <CompactGenerationReadinessPanel
        locale={locale}
        report={report}
        onProceed={handleProceed}
        onRetry={handleRetry}
      />
    </>
  )
}
